<template>
  <fullscreen-dialog
    v-model="model"
    :title="`${$t('report')} ${toLatin(employee.name)} ${toLatin(employee.lastname)}`"
    :subtitle="`${$t('month')}: ${month}/${year}`"
    color="primary"
    hide-footer
    hide-padding
  >
    <template v-slot:filters>
      <page-toolbar :searchable="false">
        <template v-slot:filters>
          <v-col cols="auto">
            <month-picker
              v-model="date"
              color="white"
              hide-details
              @updatedMonthDates="monthDates = $event"
            ></month-picker>
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="search"
              kind="search"
              :placeholder="$t('search')"
              color="white"
              hide-details
            ></text-field>
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              :label="$t('show_all_days')"
              hide-details
              v-model="allDayFirm"
              dense
            ></v-checkbox>
          </v-col>
          <v-col>
            <v-row no-gutters justify="end" align="center">
              <excel-button @click="exportExcel" class="mr-2"></excel-button>
              <print-button @click="printReport" :loading="isPrinting"></print-button>
            </v-row>
          </v-col>
        </template>
      </page-toolbar>
    </template>

    <v-simple-table v-if="userReport" dense class="ordera-box-shadow">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              {{ $t("date") }}
            </th>
            <th class="text-left">
              {{ $t("shifts") }}
            </th>
            <th class="text-left">
              {{ $t("title") }}
            </th>
            <th class="text-left">
              {{ $t("quantity") }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(item, index1) in orders">
            <tr v-if="!item.orders.length">
              <td colspan="4">{{ formatDateDisplay(item.date) }}.</td>
            </tr>
            <template v-for="(order, index2) in item.orders">
              <tr v-for="(food, index3) in filterFoods(order.foods)">
                <td v-if="index3 === 0" :rowspan="order.foods.length">
                  {{ formatDateDisplay(item.date) }}.
                </td>
                <td>{{ food.shift }}</td>
                <td>{{ toLatin(food.meal_name) }}</td>
                <td>{{ food.quantity }}</td>
              </tr>
            </template>
          </template>

          <tr>
            <td class="font-weight-bold">Ukupno</td>
            <td colspan="2"></td>
            <td class="font-weight-bold">
              {{ foodTotal }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import userService from "@/services/user";
import moment from "moment";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  mixins: [hasModel, hasExcel, hasPrinting],
  components: {
    FullscreenDialog,
  },
  props: {
    employee: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    userReportPlane: [],
    userReport: [],
    date: new Date().toISOString().substr(0, 10), // YYYY-MM format
    monthDates: [],
    search: "",
    allDayFirm: false,
  }),
  created() {
    this.date = this.dates.from;
  },
  computed: {
    foodTotal() {
      return this.userReportPlane.reduce((sum, item) => {
        return sum + item.quantity;
      }, 0);
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    orders() {
      if (!this.allDayFirm) return this.userReport;
      const year = this.year;
      const month = this.month;
      const daysInMonth = moment({ year, month: month - 1 }).daysInMonth();
      let grouped = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = moment({ year, month: month - 1, day: d }).format("YYYY-MM-DD");
        const dateOrder = this.userReport.find((g) => g.date === dateStr);
        if (!dateOrder) {
          grouped.push({
            date: dateStr,
            orders: [],
          });
        } else {
          grouped.push({ ...dateOrder });
        }
      }
      return grouped;
    },
    year() {
      return moment(this.date).format("YYYY");
    },
    month() {
      return moment(this.date).format("MM");
    },
  },
  methods: {
    formatDateDisplay(dateStr) {
      return moment(dateStr).format("DD");
    },
    filterFoods(foods) {
      if (!this.search) {
        return foods;
      }
      return foods.filter((food) => {
        return this.translate(food.meal_name)
          .toLowerCase()
          .includes(this.translate(this.search).toLowerCase());
      });
    },
    exportExcel() {
      this.setExcel();

      const headers = [
        { text: this.$t("date"), value: "date", align: "center" },
        { text: this.$t("shifts"), value: "shift", align: "center" },
        { text: this.$t("title"), value: "meal_name", align: "start" },
        { text: this.$t("quantity"), value: "quantity", align: "center" },
      ];

      const items = this.userReportPlane.map((item) => ({
        date: this.formatDateDisplay(item.date),
        shift: item.shift,
        meal_name: item.meal_name,
        quantity: item.quantity,
      }));

      // Add total row
      items.push({
        date: "Ukupno",
        shift: "",
        meal_name: "",
        quantity: this.foodTotal,
      });

      const fileName = `${this.employee.name}_${this.employee.lastname}_${this.month}_${this.year}.xlsx`;
      const title = `${this.$t("report")} ${this.employee.name} ${
        this.employee.lastname
      }`;
      const subtitle = `${this.$t("month")}: ${this.month}/${this.year}`;

      this.downloadExcel({
        fileName,
        sheets: [
          {
            name: "Izveštaj",
            headers,
            items,
            title,
            subtitle,
          },
        ],
      });
    },
    printReport() {
      this.setPrinter();

      const headers = [
        { text: this.$t("date"), value: "date", align: "center", print: true },
        { text: this.$t("shifts"), value: "shift", align: "center", print: true },
        { text: this.$t("title"), value: "meal_name", align: "start", print: true },
        { text: this.$t("quantity"), value: "quantity", align: "center", print: true },
      ];

      const items = this.userReportPlane.map((item) => ({
        date: this.formatDateDisplay(item.date),
        shift: item.shift,
        meal_name: item.meal_name,
        quantity: item.quantity,
      }));

      // Add total row
      items.push({
        date: "Ukupno",
        shift: "",
        meal_name: "",
        quantity: this.foodTotal,
      });

      const title = `${this.$t("report")} ${this.employee.name} ${
        this.employee.lastname
      }`;
      const subtitle = `${this.$t("month")}: ${this.month}/${this.year}`;

      this.printDocument({
        pageTitle: title,
        title,
        subtitle,
        tables: [
          {
            headers,
            items,
            title: "",
            tableStyle:
              "width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 20px;",
          },
        ],
      });
    },
    getEmployeeReport() {
      userService
        .getOrdersByMonth({
          month: this.month,
          year: this.year,
          user_id: this.employee.id,
        })
        .then((res) => {
          let orders = [];
          res.data.forEach((orderItem) => {
            let findDate = orders.find((i) => i.date === orderItem.date);

            if (findDate) {
              let findOrder = findDate.orders.find((i) => i.id === orderItem.id);
              if (findOrder) {
                findOrder.foods = [...findOrder.foods, orderItem];
              } else {
                findDate.orders.push({
                  id: orderItem.id,
                  foods: [orderItem],
                });
              }
            } else {
              orders.push({
                date: orderItem.date,
                orders: [
                  {
                    id: orderItem.id,
                    foods: [orderItem],
                  },
                ],
              });
            }
          });
          this.userReport = orders;
          this.userReportPlane = orders.flatMap((item) => {
            return item.orders.flatMap((order) => {
              return order.foods.map((food) => ({
                date: item.date,
                id: order.id,
                shift: food.shift,
                meal_name: food.meal_name,
                quantity: food.quantity,
                category_name: food.category_name,
              }));
            });
          });
        });
    },
  },
  watch: {
    employee: {
      handler() {
        if (this.employee.id) {
          this.getEmployeeReport();
        }
      },
      deep: true,
    },
    date() {
      this.getEmployeeReport();
    },
  },
};
</script>
